import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useEntityList, updateEntity } from '../data/entities';

// The HITL review queue. A pipeline's control.review block pauses a document
// here until a human decides; the runner polls the doc. Rules only allow
// writing the decision fields, and only while status is 'pending' — approve
// (optionally with edits) sends the document downstream, reject kills the
// branch (downstream blocks SKIP).
function ReviewCard({ item }) {
  const [edited, setEdited] = useState(null); // null = untouched
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const { user: me } = useAuth();

  const decide = async (status) => {
    setErr('');
    try {
      await updateEntity('reviewItem', {}, item.id, {
        status,
        ...(edited !== null && edited !== item.content ? { editedContent: edited } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        decidedBy: me?.email || me?.uid || '',
        decidedAt: Date.now(),
      });
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="cs-card" style={{ padding: 14 }}>
      <p className="cs-help" style={{ margin: 0, fontSize: 12 }}>
        pipeline <Link to={`/pipelines/${item.source?.pipelineId}`}>{item.source?.pipelineId}</Link>
        {' · '}block {item.source?.blockId} · {new Date(item.createdAt).toLocaleString()}
      </p>
      {item.instructions && (
        <p className="cs-help" style={{ margin: '8px 0 0', fontStyle: 'italic' }}>{item.instructions}</p>
      )}
      <textarea
        className="cs-input"
        style={{ width: '100%', minHeight: 140, marginTop: 10, fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}
        value={edited !== null ? edited : item.content || ''}
        onChange={(e) => setEdited(e.target.value)}
      />
      <input
        className="cs-input"
        style={{ width: '100%', marginTop: 8 }}
        placeholder="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {err && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)' }}>{err}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => decide('approved')}>
          Approve{edited !== null && edited !== item.content ? ' with edits' : ''}
        </button>
        <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => decide('rejected')}>
          Reject (kill branch)
        </button>
      </div>
    </div>
  );
}

export default function Review() {
  const { docs: items, error } = useEntityList('reviewItem', {});
  const pending = (items || []).filter((r) => r.status === 'pending');
  const decided = (items || []).filter((r) => r.status !== 'pending').slice(0, 20);

  return (
    <div className="cs-page">
      <h1 style={{ marginBottom: 4 }}>Review queue</h1>
      <p className="cs-help">
        Documents a pipeline's review gate is holding for a human decision. The run waits on you.
      </p>
      {error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)' }}>{error}</p>}
      {items === null && <p className="cs-help">Loading…</p>}
      {items !== null && pending.length === 0 && <p className="cs-help">Nothing waiting. ✓</p>}
      <div style={{ display: 'grid', gap: 12 }}>
        {pending.map((item) => <ReviewCard key={item.id} item={item} />)}
      </div>

      {decided.length > 0 && (
        <details style={{ marginTop: 20 }}>
          <summary className="cs-help" style={{ cursor: 'pointer' }}>Recent decisions ({decided.length})</summary>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {decided.map((r) => (
              <p key={r.id} className="cs-help" style={{ margin: 0, fontSize: 12 }}>
                {r.status === 'approved' ? '✓' : '✗'} {r.status}
                {r.editedContent ? ' (edited)' : ''} · {r.decidedBy || 'unknown'} ·{' '}
                {r.decidedAt ? new Date(r.decidedAt).toLocaleString() : ''}
                {r.note ? ` — ${r.note}` : ''}
              </p>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
